import { SMSAutoResponse, ApplicationAccessType, CommunicationType } from 'domains/content/types';
import { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText';
import { useState, useCallback, useEffect } from 'react';

type UseSmsFormProps = {
    initialApplicationsAccess?: ApplicationAccessType[];
    initialName?: string;
    initialCommunicationType?: CommunicationType;
    initialContent?: string;
    initialAutoResponses?: SMSAutoResponse[];
    isSavedSmsContentLoading: boolean;
    initialStatus?: ContentStatus;
};

const useSmsForm = ({
    initialApplicationsAccess = [ApplicationAccessType.OUTREACH],
    initialName = 'Untitled',
    initialCommunicationType = CommunicationType.COMMERCIAL,
    initialContent = '',
    initialAutoResponses,
    isSavedSmsContentLoading,
    initialStatus = ContentStatus.DRAFT,
}: UseSmsFormProps) => {
    // Form state
    const [name, setName] = useState(initialName);
    const [content, setContent] = useState(initialContent);
    const [autoResponses, setAutoResponses] = useState(initialAutoResponses);
    const [isNameTouched, setIsNameTouched] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [communicationType, setCommunicationType] = useState(initialCommunicationType);
    const [applicationsAccess, setApplicationsAccess] = useState(initialApplicationsAccess);

    // Update form when initialValues change
    useEffect(() => {
        if (initialName && initialName !== 'Untitled') {
            setName(initialName);
        }
        if (initialContent) {
            setContent(initialContent);
        }
        if (initialAutoResponses) {
            setAutoResponses(initialAutoResponses);
        }
        // We only want this to run once, after the SMS content has loaded
        // If we have the time, we should refactor SMS to use a reducer instead of local state
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSavedSmsContentLoading]);

    // Validation
    const isNameUndefinedOnTouch = !name && isNameTouched;
    const isNameTooShort = isNameTouched && name.length < 3;

    // Helper messages
    const nameHelperText = isNameUndefinedOnTouch
        ? 'Please enter an SMS name'
        : isNameTooShort
        ? 'SMS name must have at least 3 characters'
        : '';

    // Input handlers
    const handleChangeName = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newName = e.target.value;
            setName(newName);
            if (!isNameTouched) setIsNameTouched(true);
        },
        [isNameTouched]
    );

    const handleCommunicationTypeChange = (e: any) => {
        setCommunicationType(e.target.value);
    };

    const handleApplicationsAccessChange = (e: any) => {
        setApplicationsAccess(e.target.value);
    };

    return {
        applicationsAccess,
        name,
        communicationType,
        content,
        autoResponses,
        isNameUndefinedOnTouch,
        isNameTooShort,
        nameHelperText,
        errorMessage,
        setContent,
        setAutoResponses,
        setErrorMessage,
        handleChangeName,
        handleApplicationsAccessChange,
        handleCommunicationTypeChange,
    };
};

export default useSmsForm;
